import moment from 'moment';
import { Pagination, FormLabel, Grid, Stack, Typography, Box, Tooltip, Button, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha, useTheme } from '@mui/material/styles';
import { debounce } from '@mui/material/utils';
import { sanitizeHtml } from 'lib/sanitizeHtml';
import Link from 'components/common/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { sendApiRequest } from 'lib/api/public';
import notify from 'lib/notify';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { NextRouter } from 'next/router';
import { IAttachment } from 'components/types';
import { surfaceBorderSx } from 'components/common/surfaces';

type FieldProps = {
  label: string;
  renderControl: () => React.ReactNode;
  isCompact?: boolean;
  newLines?: boolean;
  required?: boolean;
  id?: string;
  testId?: string;
  flexOptions?: {
    label?: number;
    value?: number;
    direction?: 'row' | 'column';
  };
};

export const getFieldDescription = (id: string, isGroup = false) => {
  // TODO: Remove this after we have descriptions for all fields
  return null;
};

export const renderField = ({
  id,
  label,
  renderControl,
  isCompact = false,
  required = false,
  newLines = true,
  flexOptions = {},
}: FieldProps) => {
  return (
    <>
      <Grid
        container
        flexDirection={{ xs: 'column', sm: 'column', md: flexOptions.direction || 'row' }}
        sx={flexOptions.direction !== 'column' ? { alignItems: 'flex-start', gap: { xs: 1, sm: 1, md: 0 } } : undefined}
      >
        <Grid
          flex={flexOptions.label ? flexOptions.label : 1}
          id={`label-${label}`}
          sx={{ pt: { xs: 0, sm: 0, md: 1 }, pr: { xs: 0, sm: 0, md: 1 } }}
        >
          <FormLabel
            sx={theme => ({ color: `${theme?.palette?.text?.primary} !important`, fontWeight: '600' })}
            required={required}
          >
            {label}
          </FormLabel>
        </Grid>
        <Grid
          flex={flexOptions.value ? flexOptions.value : isCompact ? 6 : 4}
          sx={{ position: 'relative', width: { xs: '100%', sm: '100%', md: 'auto' } }}
        >
          {renderControl()}
          {id ? getFieldDescription(id) : null}
        </Grid>
      </Grid>

      {newLines && (
        <>
          <br />
        </>
      )}
    </>
  );
};

const TablePaginationBar = ({
  router,
  totalCount,
  page,
  extraQueryParams = {},
  pageParamName = 'page',
}: {
  router: NextRouter;
  totalCount: number;
  page: number;
  extraQueryParams?: Record<string, string | number | boolean>;
  pageParamName?: string;
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const handlePageChange = (_e: React.ChangeEvent<unknown>, value: number) => {
    const query = router.query;

    if (query.sortField && !extraQueryParams.sortField) {
      extraQueryParams.sortField = query.sortField as string;
    }

    if (query.sortDirection && !extraQueryParams.sortDirection) {
      extraQueryParams.sortDirection = query.sortDirection as string;
    }

    void router.push({
      query: {
        ...query,
        [pageParamName]: value,
        ...extraQueryParams,
      },
    });
  };

  const startIndex = (page - 1) * 50 + 1;
  const endIndex = Math.min(page * 50, totalCount);

  return (
    <Stack
      spacing={isXs ? 1 : 2}
      alignItems="center"
      direction="row"
      sx={{ marginTop: '20px', flexWrap: 'wrap', rowGap: 1 }}
    >
      <Pagination
        shape="rounded"
        variant="outlined"
        color="primary"
        size={isXs ? 'small' : 'medium'}
        siblingCount={isXs ? 0 : 1}
        boundaryCount={1}
        count={Math.ceil(totalCount / 50)}
        page={page}
        onChange={handlePageChange}
        sx={{ display: 'flex', justifyContent: 'center' }}
      />

      {totalCount > 0 && (
        <Typography
          variant="body1"
          sx={theme => ({
            padding: { xs: 0, sm: '0 10px' },
            margin: { xs: 0, sm: '0 10px' },
            fontWeight: '500',
            color: theme?.palette?.text?.primary,
          })}
        >
          Results: {startIndex}-{endIndex} of {totalCount}
        </Typography>
      )}
    </Stack>
  );
};

export const renderPagination = ({
  router,
  totalCount,
  page,
  extraQueryParams = {},
  pageParamName = 'page',
}: {
  router: NextRouter;
  totalCount: number;
  page: number;
  extraQueryParams?: Record<string, string | number | boolean>;
  pageParamName?: string;
}) => {
  return (
    <TablePaginationBar
      router={router}
      totalCount={totalCount}
      page={page}
      extraQueryParams={extraQueryParams}
      pageParamName={pageParamName}
    />
  );
};

export const capitalize = (string: string) => {
  if (!string) return '';

  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const cleanHtml = (html = '') => {
  return html.replace(/<p>&nbsp;<\/p>/g, '');
};

export const isDeepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, i) => isDeepEqual(item, b[i]));
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(
    key =>
      Object.prototype.hasOwnProperty.call(b, key) &&
      isDeepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
  );
};

export const formatAccountRoleLabel = (role?: string) => {
  if (role === 'superAdmin') return 'Admin';
  if (role === 'admin') return 'Manager';
  if (role === 'recruiter') return 'Recruiter';
  return role || '';
};

/** Display name for an account user, with a `(deleted)` suffix when they were removed from the account. */
export const formatUserDisplayName = (
  user?: { firstName?: string; lastName?: string; email?: string; status?: string } | null,
): string => {
  if (!user) {
    return '';
  }
  const name = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email || '';
  return user.status === 'deleted' ? `${name} (deleted)` : name;
};

export const formatUserDateTime = (date: string | number | Date | moment.Moment) => {
  if (!date) return '';

  const momentDate = moment.isMoment(date) ? date : moment(date);

  const userTimeFormat = typeof window !== 'undefined' ? localStorage.getItem('userTimeFormat') : null;
  const userDateFormat = typeof window !== 'undefined' ? localStorage.getItem('userDateFormat') : null;

  const timeFormat = userTimeFormat === '24h' ? 'HH:mm' : 'hh:mm A';
  const dateFormat = userDateFormat || 'MMM DD, YYYY';

  return momentDate.format(`${dateFormat} ${timeFormat}`);
};

export const formatUserDate = (date?: string | number | Date | moment.Moment | null) => {
  if (!date) {
    return '';
  }

  const momentDate = moment.isMoment(date) ? date : moment(date);
  const userDateFormat = typeof window !== 'undefined' ? localStorage.getItem('userDateFormat') : null;
  const dateFormat = userDateFormat || 'MMM DD, YYYY';
  return momentDate.format(dateFormat);
};

export const formatUserTime = (date: string | number | Date | moment.Moment) => {
  if (!date) {
    return '';
  }

  const momentDate = moment.isMoment(date) ? date : moment(date);
  const userTimeFormat = typeof window !== 'undefined' ? localStorage.getItem('userTimeFormat') : null;
  const timeFormat = userTimeFormat === '24h' ? 'HH:mm' : 'hh:mm A';
  return momentDate.format(timeFormat);
};

export const getTablePreferences = (tableName: string): Record<string, unknown> => {
  if (typeof window === 'undefined') {
    return {};
  }

  const localValue = localStorage.getItem('tablePreferences');
  const preferences = JSON.parse(localValue || '{}') as Record<string, unknown>;

  return (preferences[tableName] || {}) as Record<string, unknown>;
};

export const setTablePreferences = ({
  tableName,
  values,
}: {
  tableName: string;
  values: { key: string; value: unknown }[];
}) => {
  if (typeof window === 'undefined') {
    return;
  }

  const localValue = localStorage.getItem('tablePreferences');

  const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  };

  const parsedPreferences: unknown = JSON.parse(localValue || '{}');
  let preferences = isRecord(parsedPreferences) ? parsedPreferences : {};

  // Clear preferences if it's an array
  if (Array.isArray(preferences)) {
    preferences = {};
  }

  const currentTablePreferences = preferences[tableName];
  const tablePreferences: Record<string, unknown> = isRecord(currentTablePreferences) ? currentTablePreferences : {};

  preferences[tableName] = tablePreferences;

  values.forEach(({ key, value }) => {
    tablePreferences[key] = value;
  });

  localStorage.setItem('tablePreferences', JSON.stringify(preferences));

  debouncedSaveTablePreferences(tableName);
};

const debouncedSaveTablePreferences = debounce((tableName: string) => {
  const tablePrefs = getTablePreferences(tableName);
  const preferences = Object.entries(tablePrefs).map(([key, value]) => ({ key, value }));

  sendApiRequest({
    path: '/users/save-table-preferences',
    data: {
      tableName,
      preferences,
    },
  })
    .then(() => {
      console.log('Table preferences saved');
    })
    .catch(err => {
      console.error(err);
    });
}, 500);

export const validateEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateUrl = (url: string) => {
  let urlString = url;

  if (!urlString || !urlString.trim()) {
    return false;
  }

  urlString = urlString.trim();

  if (urlString.startsWith('https://') || urlString.startsWith('http://')) {
    return true;
  }

  return false;
};

export const commonPreviewDialogStyle = {
  width: {
    xs: '100%',
    md: '60%',
  },
  left: 'inherit',
};

const getFileSortTime = (file: IAttachment): number => {
  if (!file.date) {
    return 0;
  }

  const time = new Date(file.date).getTime();

  return Number.isNaN(time) ? 0 : time;
};

export const sortFilesByDate = (files: IAttachment[]) => {
  return [...(files || [])]
    .map((file, index) => ({ file, index }))
    .sort((a, b) => {
      const timeA = getFileSortTime(a.file);
      const timeB = getFileSortTime(b.file);

      if (timeB !== timeA) {
        return timeB - timeA;
      }

      return b.index - a.index;
    })
    .map(({ file }) => file);
};

export interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  style?: React.CSSProperties;
  isHidden?: boolean;
  /** Keep children mounted when inactive (avoids refetch/remount flicker on tab switch). */
  keepMounted?: boolean;
}

export const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, style, keepMounted = false, ...other } = props;
  const isActive = value === index;

  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={style}
    >
      {(keepMounted || isActive) && <Box>{children}</Box>}
    </div>
  );
};

export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

export const copyableBox = (
  link: string,
  hideLink = false,
  copyTestId?: string,
  openExternalTestId?: string,
  onOpenExternalClick?: () => void,
  wrap = false,
) => {
  return (
    <Box
      sx={theme => ({
        backgroundColor: theme.palette.action.hover,
        borderRadius: 1,
        p: 1,
        display: 'flex',
        alignItems: wrap ? 'flex-start' : 'center',
        gap: 1,
        border: `1px solid ${theme.palette.divider}`,
        '&:hover': {
          backgroundColor: theme.palette.action.selected,
        },
      })}
    >
      <Typography
        component="span"
        sx={theme => ({
          fontFamily: 'Monaco, Consolas, "Roboto Mono", monospace',
          fontSize: '0.875rem',
          color: theme.palette.text.primary,
          flex: 1,
          minWidth: 0,
          ...(wrap
            ? {
                overflow: 'visible',
                whiteSpace: 'normal',
                wordBreak: 'break-all',
                lineHeight: 1.5,
                pt: 0.5,
              }
            : {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }),
        })}
      >
        {link}
      </Typography>
      <Tooltip title="Copy">
        <span style={{ display: 'inline-block' }}>
          <CopyToClipboard text={link} onCopy={() => notify('Copied to clipboard')}>
            <Button
              color="secondary"
              size="small"
              data-testid={copyTestId}
              sx={theme => ({
                minWidth: 0,
                p: 0.75,
                height: 34,
                minHeight: 34,
                boxSizing: 'border-box',
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.secondary.contrastText,
                  borderColor: theme.palette.secondary.main,
                },
              })}
            >
              <ContentCopyIcon sx={{ fontSize: 18 }} />
            </Button>
          </CopyToClipboard>
        </span>
      </Tooltip>

      {!hideLink && (
        <Link
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={openExternalTestId}
          onClick={() => {
            onOpenExternalClick?.();
          }}
          sx={theme => ({
            color: theme.palette.text.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0.75,
            height: 34,
            minHeight: 34,
            boxSizing: 'border-box',
            borderRadius: 1,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastText,
              borderColor: theme.palette.secondary.main,
            },
          })}
        >
          <OpenInNewIcon sx={{ fontSize: 18 }} />
        </Link>
      )}
    </Box>
  );
};

export const detailPaperStyle = {
  padding: '10px 14px',
  marginBottom: '10px',
  flexShrink: 0,
  ...surfaceBorderSx,
};

export const primaryInfoWrapperStyle = (theme: Theme) => ({
  py: 1,
  px: 2,
  background: theme.palette.action.hover,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderTopLeftRadius: '6px',
  borderTopRightRadius: '6px',
  flexWrap: 'wrap',
  gap: 1.5,
});

export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const reorder = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const move = <T,>(source: T[], destination: T[], sourceIndex: number, destinationIndex: number) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(sourceIndex, 1);
  destClone.splice(destinationIndex, 0, removed);
  return { source: sourceClone, destination: destClone };
};

export const scrollableContainerStyle = (height: number = 100) => ({
  overflowY: 'auto',
  maxHeight: `calc(100dvh - ${height}px)`,
});

export const getNavigationTreeLineColor = (theme: Theme, onDarkSurface = false) => {
  return onDarkSurface ? theme.palette.grey[700] : theme.palette.grey[600];
};

export const getNavigationDividerColor = (theme: Theme, onDarkSurface = false) => {
  const baseColor = onDarkSurface ? theme.palette.secondary.contrastText : theme.palette.text.primary;
  return alpha(baseColor, onDarkSurface ? 0.14 : 0.22);
};

// Utility function to strip HTML tags (sanitize first to avoid XSS, then read text)
export const stripHtml = (html: string) => {
  const safe = sanitizeHtml(html);
  const div = document.createElement('div');
  div.innerHTML = safe;
  return div.textContent || div.innerText || '';
};

export const commonRightModalProps = {
  fullScreen: true,
  sx: {
    width: {
      xs: '100%',
      md: '902px',
    },
    left: 'inherit',
  },
};

export const tabTitle = (title: string) => {
  return (
    <Typography variant="h6" sx={{ pb: 1, mb: 1, fontSize: '16px' }}>
      {title}
    </Typography>
  );
};

export const getSubdomain = () => {
  try {
    // Use location.origin instead of href
    const origin = window.location.origin;
    // Remove protocol (http[s]://)
    const [, rest] = origin.match(/^https?:\/\/(.+)/) || [];
    // Split by "." and get subdomain (before first ".")
    return rest?.split('.')[0] || '';
  } catch {
    return '';
  }
};
